import React, { useRef, useState } from 'react';
import { DayPicker, DateRange } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import './datePicker.scss';
import { svgs } from '../../../common/assats';
import TextInputWithIcon from '../../TextInputWithIcon';
import { useOutsideAlerter } from '../../../common/hooks/UseClickOutside';
import { useAlert } from '../../../common/contexts/AlertContext';

interface DatePickerProps {
    selectedRange: DateRange | undefined;
    setSelectedRange: (val: DateRange | undefined) => void;
}

const DatePicker: React.FC<DatePickerProps> = ({ selectedRange, setSelectedRange }) => {
    const [showCalendar, setShowCalendar] = useState<boolean>(false);
    const [tempRange, setTempRange] = useState<DateRange | undefined>(selectedRange);
    const [manualInput, setManualInput] = useState<string>('');
    const wrapperRef = useRef(null);
    const alert = useAlert();


    useOutsideAlerter(wrapperRef, () => setShowCalendar(false));

    const formatDate = (date: Date): string => {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };
    const formatDateRange = () => {
        if (!tempRange?.from || !tempRange?.to) return 'Select creation date';
        return `${formatDate(tempRange.from)} - ${formatDate(tempRange.to)}`;
    };
    const parseDateParts = (parts: number[]): Date => {
        return new Date(parts[2], parts[1] - 1, parts[0]);
    };
    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let value = event.target.value.trim();
        value = value.replace(/[^0-9/-]/g, ""); // Allow only numbers, slashes, and dashes
        setManualInput(value);

        const datePattern = /^\d{1,2}\/\d{1,2}\/\d{4}\s*-\s*\d{1,2}\/\d{1,2}\/\d{4}$/; // Format: DD/MM/YYYY - DD/MM/YYYY
        if (datePattern.test(value)) {
            const [fromStr, toStr] = value.split(/\s*-\s*/); // Handle optional spaces

            // Convert DD/MM/YYYY → YYYY-MM-DD for correct parsing
            const fromParts = fromStr.split('/').map(Number);
            const toParts = toStr.split('/').map(Number);

            if (fromParts.length === 3 && toParts.length === 3) {
                const fromDate = parseDateParts(fromParts);
                const toDate = parseDateParts(toParts);

                if (!isNaN(fromDate.getTime()) && !isNaN(toDate.getTime())) {
                    if (fromDate <= toDate) {
                        const newRange = { from: fromDate, to: toDate };
                        setTempRange(newRange);
                    } else {
                        alert("Invalid date range: 'from' date must be before 'to' date.", "error");
                        setTempRange(undefined); // clear invalid range
                    }
                }
            }
        } else {
            alert("Invalid format. Please use DD/MM/YYYY - DD/MM/YYYY.", "error");
        }
    };


    const handleApply = () => {
        if (tempRange?.from && tempRange?.to) {
            const from = tempRange.from;
            const to = tempRange.to;
            if (from <= to) {
                setSelectedRange(tempRange);
                setShowCalendar(false);
            } else {
                alert("Invalid date range: 'from' date must be before 'to' date.", "error");
                return;
            }
        }
        else {
            alert("Please select a valid date range.", "error");
        }
    };

    const handleCancel = () => {
        setTempRange(undefined);
        setSelectedRange(undefined);
        setShowCalendar(false);
        setManualInput('');
    };

    return (
        <div className="date-picker" ref={wrapperRef}>
            <div className="date-input">
                <TextInputWithIcon
                    Icon={svgs.icons.CalenderTime}
                    placeholder="Select creation date"
                    setValue={setManualInput}
                    value={manualInput || formatDateRange()}
                    inputName="dateRange"
                    extraStyles={{ height: "2.5rem" }}
                    onClick={() => setShowCalendar(!showCalendar)}
                    onChange={handleInputChange} // Allow manual date entry
                />
            </div>
            {showCalendar && (
                <div className="calendar-popup">
                    <DayPicker
                        mode="range"
                        selected={tempRange}
                        onSelect={setTempRange}
                        className="date-range"
                        showOutsideDays
                    />
                    <div className="calendar-footer">
                        <button onClick={handleCancel}>Cancel</button>
                        <button onClick={handleApply}>Apply</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DatePicker;
